import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { format } from 'date-fns'
import {
  BanIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  CircleIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IPermitTypeField,
  IStepSubmission,
  PermitFieldType,
} from '~/client/graph/types.generated'
import {
  CardListSummaryBar,
  ICardListGroup,
  ICardListGroupConfig,
  ICardListItem,
  useCardListCore,
} from '~/client/src/shared/components/CardList'
import { ListHeader } from '~/client/src/shared/components/ListHeader'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import {
  isHiddenFieldType,
  ReadonlyFieldRow,
} from '~/client/src/shared/components/ReadonlyFieldRenderer'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import PermitField from '~/client/src/shared/models/PermitField'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import {
  DIRECT_ASSIGNMENT_KEY,
  ISubmissionGroup,
} from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import './SubmissionStatusList.scss'

enum SortOption {
  Name = 'name',
  Status = 'status',
}

const getSortOptionLabels = (): Record<SortOption, string> => ({
  [SortOption.Name]: Localization.translator.name,
  [SortOption.Status]: Localization.translator.status,
})

interface IStores {
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  projectDateStore?: ProjectDateStore
  state?: InitialState
  userProjectsStore?: UserProjectsStore
}

interface IProps extends IStores {
  submissionGroups: ISubmissionGroup[]
  template?: WorkflowType
  stepId?: string
}

interface ISubmissionUserItem extends ICardListItem {
  id: string
  user: User
  groupKey: string
  fieldType: string
  fieldCaption: string
  isApproved: boolean
  submission?: IStepSubmission
  requiredCount?: number
  approvedCount: number
  totalCount: number
}

function flattenSubmissionGroups(
  submissionGroups: ISubmissionGroup[],
): ISubmissionUserItem[] {
  const items: ISubmissionUserItem[] = []

  submissionGroups.forEach(group => {
    const {
      approvedUsers,
      pendingUsers,
      fieldType,
      fieldCaption,
      ruleId,
      companyId,
      requiredCount,
    } = group
    const totalCount = approvedUsers.length + pendingUsers.length
    const approvedCount = approvedUsers.length
    // Rules and companies are keyed by id, so two sharing a name stay apart
    const groupKey = ruleId ?? companyId ?? fieldCaption

    approvedUsers.forEach(userData => {
      items.push({
        id: `${groupKey}-${userData.user.id}-approved`,
        user: userData.user,
        groupKey,
        fieldType: String(fieldType),
        fieldCaption,
        isApproved: true,
        submission: userData.submission,
        requiredCount,
        approvedCount,
        totalCount,
      })
    })

    pendingUsers.forEach(user => {
      items.push({
        id: `${groupKey}-${user.id}-pending`,
        user,
        groupKey,
        fieldType: String(fieldType),
        fieldCaption,
        isApproved: false,
        submission: undefined,
        requiredCount,
        approvedCount,
        totalCount,
      })
    })
  })

  return items
}

interface IGroupHeaderProps {
  group: ICardListGroup<ISubmissionUserItem>
  onToggle: () => void
}

const SubmissionGroupHeader: React.FC<IGroupHeaderProps> = ({
  group,
  onToggle,
}) => {
  const firstItem = group.items[0]
  if (!firstItem) return null

  const { fieldType, fieldCaption } = firstItem
  // A rule needs one sign-off; sign-offs that count toward nothing show as they are
  const totalCount = firstItem.requiredCount || group.items.length
  const approvedCount = Math.min(
    group.items.filter(item => item.isApproved).length,
    totalCount,
  )
  const isAllComplete = approvedCount === totalCount && totalCount > 0
  const isPartialComplete = approvedCount > 0 && approvedCount < totalCount

  const getStatusIcon = () => {
    if (isAllComplete) return CircleCheckIcon
    if (isPartialComplete) return BanIcon
    return CircleIcon
  }

  const StatusIcon = getStatusIcon()
  const ChevronIcon = group.isCollapsed ? ChevronRightIcon : ChevronDownIcon

  return (
    <div
      className="submission-status-header row y-center pointer"
      onClick={onToggle}
    >
      <ChevronIcon size={16} className="no-grow mr5 text" />
      <PermitFieldIcon
        fieldName={
          fieldType === DIRECT_ASSIGNMENT_KEY ? PermitFieldType.User : fieldType
        }
        className="tw:mr-2.5"
      />
      <span className="text large bold">{fieldCaption}</span>
      <span className="row x-end y-center">
        <span
          className={classList({
            'text large end': true,
            light: !isAllComplete,
          })}
        >
          {Localization.translator.xOfY(approvedCount, totalCount)}
        </span>
        <StatusIcon
          size={16}
          className={classList({
            'no-grow ml5 text': true,
            'success-green': isAllComplete,
            light: !isAllComplete,
          })}
        />
      </span>
    </div>
  )
}

interface IUserSubmissionRowProps extends IStores {
  item: ISubmissionUserItem
  template?: WorkflowType
  stepId?: string
}

const UserSubmissionRow: React.FC<IUserSubmissionRowProps> = ({
  item,
  template,
  stepId,
  projectMembersStore,
  companiesStore,
  locationAttributesStore,
  materialsStore,
  projectDateStore,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { user, isApproved, submission } = item

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const getTypeFieldForField = useCallback(
    (field: PermitField): IPermitTypeField | undefined => {
      if (!template || !stepId) return undefined

      const step = template.workflowSteps.find(s => s.id === stepId)
      if (!step) return undefined

      const directField = step.fields?.find(f => f.id === field.fieldId)
      if (directField) return directField

      const conditionalField = step.conditionalFields
        ?.flatMap(cf => cf.values || [])
        .find(f => f.id === field.fieldId)
      if (conditionalField) return conditionalField

      for (const s of template.workflowSteps) {
        const found = s.fields?.find(f => f.id === field.fieldId)
        if (found) return found
        const foundCond = s.conditionalFields
          ?.flatMap(cf => cf.values || [])
          .find(f => f.id === field.fieldId)
        if (foundCond) return foundCond
      }

      return undefined
    },
    [template, stepId],
  )

  const getWorkflowFieldOrder = useCallback((): Map<string, number> => {
    if (!template || !stepId) return new Map()

    const step = template.workflowSteps.find(s => s.id === stepId)
    if (!step) return new Map()

    const orderMap = new Map<string, number>()
    let index = 0

    step.fields?.forEach(f => {
      orderMap.set(f.id, index++)
    })

    step.conditionalFields?.forEach(cf => {
      cf.values?.forEach(f => {
        orderMap.set(f.id, index++)
      })
    })

    return orderMap
  }, [template, stepId])

  const renderSubmissionFields = useCallback(() => {
    if (!submission) return null

    const rawFields = submission.fields || []
    const submittedAtDate = new Date(submission.submittedAt)
    const formattedDate = format(submittedAtDate, 'MMM d, yyyy h:mm a')

    const isPermitField = (f: unknown): f is PermitField =>
      f !== null && typeof f === 'object' && 'fieldId' in f

    const fields = rawFields.filter(isPermitField)

    const visibleFields = fields.filter(field => {
      if (!field.values?.length) return false
      const typeField = getTypeFieldForField(field)
      if (!typeField) return true
      return !isHiddenFieldType(typeField.type)
    })

    const fieldOrder = getWorkflowFieldOrder()
    const sortedFields = [...visibleFields].sort((a, b) => {
      const orderA = fieldOrder.get(a.fieldId) ?? Number.MAX_SAFE_INTEGER
      const orderB = fieldOrder.get(b.fieldId) ?? Number.MAX_SAFE_INTEGER
      return orderA - orderB
    })

    const hasVisibleFields = sortedFields.length > 0

    return (
      <div className="submission-fields col">
        <div className="text extra-small light bb-light-grey">
          {Localization.translator.submittedAt}: {formattedDate}
        </div>
        {hasVisibleFields ? (
          <div className="col">
            {sortedFields.map(field => {
              const typeField = getTypeFieldForField(field)
              if (!typeField) return null

              return (
                <ReadonlyFieldRow
                  key={field.instanceId}
                  values={field.values}
                  typeField={typeField}
                  projectMembersStore={projectMembersStore}
                  companiesStore={companiesStore}
                  locationAttributesStore={locationAttributesStore}
                  materialsStore={materialsStore}
                  projectDateStore={projectDateStore}
                />
              )
            })}
          </div>
        ) : (
          <div className="text small light">
            {Localization.translator.noFieldsSubmitted}
          </div>
        )}
      </div>
    )
  }, [
    submission,
    getTypeFieldForField,
    getWorkflowFieldOrder,
    projectMembersStore,
    companiesStore,
    locationAttributesStore,
    materialsStore,
    projectDateStore,
  ])

  if (!isApproved) {
    return (
      <div className="submission-user-row row y-center">
        <UserProfilePreview
          user={user}
          isUserNameClickable
          withCompany
          withRoles
          className="pending-user"
        />
        <span className="no-grow ml-auto text light pr16">
          <CircleIcon size={16} className="tw:block" />
        </span>
      </div>
    )
  }

  const hasSubmission = !!submission
  const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon

  return (
    <div className="submission-user-row col">
      <div
        className={classList({
          'row y-center': true,
          pointer: hasSubmission,
        })}
        onClick={hasSubmission ? toggleExpanded : undefined}
      >
        {hasSubmission && (
          <ChevronIcon size={16} className="no-grow mr5 text small" />
        )}
        <UserProfilePreview
          user={user}
          isUserNameClickable
          withCompany
          withRoles
        />
        <span className="no-grow ml-auto text success-green pr16">
          <CircleCheckIcon size={16} className="tw:block" />
        </span>
      </div>
      {isExpanded && hasSubmission && (
        <div className="submission-content mt5">{renderSubmissionFields()}</div>
      )}
    </div>
  )
}

const SubmissionStatusList: React.FC<IProps> = ({
  submissionGroups,
  template,
  stepId,
  projectMembersStore,
  companiesStore,
  locationAttributesStore,
  materialsStore,
  projectDateStore,
  state,
  userProjectsStore,
}) => {
  const [searchKey, setSearchKey] = useState('')
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.Name)
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC)
  const [isOnlyMeActive, setIsOnlyMeActive] = useState(false)
  const [isOnlyMyCompanyActive, setIsOnlyMyCompanyActive] = useState(false)

  const toggleOnlyMe = useCallback(() => {
    setIsOnlyMeActive(prev => !prev)
  }, [])

  const toggleOnlyMyCompany = useCallback(() => {
    setIsOnlyMyCompanyActive(prev => !prev)
  }, [])

  const sortOptions: IOptionItem[] = useMemo(() => {
    const labels = getSortOptionLabels()
    return Object.values(SortOption).map(option => ({
      key: option,
      label: labels[option],
      ...(option === SortOption.Name && { glyph: 'name' as const }),
    }))
  }, [])

  const currentUser = state?.user
  const currentUserCompanyId = useMemo(() => {
    if (!currentUser || !userProjectsStore) return null
    return UserProject.getCompanyId(currentUser, userProjectsStore)
  }, [currentUser, userProjectsStore])

  const allItems = useMemo(
    () => flattenSubmissionGroups(submissionGroups),
    [submissionGroups],
  )

  const filteredItems = useMemo(() => {
    const lowerSearch = searchKey.trim().toLowerCase()
    const filtered = allItems.filter(item => {
      if (isOnlyMeActive && currentUser && item.user.id !== currentUser.id) {
        return false
      }
      if (isOnlyMyCompanyActive && currentUserCompanyId && userProjectsStore) {
        const itemUserCompanyId = UserProject.getCompanyId(
          item.user,
          userProjectsStore,
        )
        if (itemUserCompanyId !== currentUserCompanyId) return false
      }
      if (
        lowerSearch &&
        !item.user.fullName.toLowerCase().includes(lowerSearch)
      ) {
        return false
      }
      return true
    })

    const isAsc = sortOrder === SortOrder.ASC
    return filtered.sort((a, b) => {
      switch (sortOption) {
        case SortOption.Name: {
          const comparison = a.user.fullName.localeCompare(b.user.fullName)
          return isAsc ? comparison : -comparison
        }
        case SortOption.Status: {
          if (a.isApproved !== b.isApproved) {
            return isAsc ? (a.isApproved ? 1 : -1) : a.isApproved ? -1 : 1
          }
          return a.user.fullName.localeCompare(b.user.fullName)
        }
        default:
          return 0
      }
    })
  }, [
    allItems,
    searchKey,
    sortOption,
    sortOrder,
    isOnlyMeActive,
    isOnlyMyCompanyActive,
    currentUser,
    currentUserCompanyId,
    userProjectsStore,
  ])

  // Rules keep the configurator's order, ahead of the groups sorted by caption
  const groupConfig: ICardListGroupConfig<ISubmissionUserItem> = useMemo(() => {
    const ruleOrder = new Map(
      submissionGroups
        .filter(group => group.ruleId)
        .map((group, index) => [group.ruleId, index]),
    )
    const captions = new Map(
      submissionGroups.map(group => [
        group.ruleId ?? group.fieldCaption,
        group.fieldCaption,
      ]),
    )

    return {
      groupBy: item => item.groupKey,
      getGroupLabel: groupKey => captions.get(groupKey) ?? groupKey,
      sortGroups: (a, b) =>
        (ruleOrder.get(a) ?? Infinity) - (ruleOrder.get(b) ?? Infinity) ||
        a.localeCompare(b),
    }
  }, [submissionGroups])

  const listState = useCardListCore({
    items: filteredItems,
    groupConfig,
  })
  const { groups, toggleGroup } = listState

  if (!submissionGroups?.length) return null

  return (
    <div>
      <ListHeader.DisplayBar
        search={{ value: searchKey, onChange: setSearchKey }}
        quickFilters={{
          isOnlyMe: isOnlyMeActive,
          isOnlyMyCompany: isOnlyMyCompanyActive,
          onToggleMe: toggleOnlyMe,
          onToggleMyCompany: toggleOnlyMyCompany,
        }}
        sorting={{
          value: { key: sortOption, order: sortOrder },
          defaultValue: { key: SortOption.Name, order: SortOrder.ASC },
          options: sortOptions,
          allowNone: false,
          onChange: value => {
            if (value.key) setSortOption(value.key as SortOption)
            setSortOrder(value.order ?? SortOrder.ASC)
          },
        }}
      />
      <CardListSummaryBar listState={listState}>
        {Localization.translator.xUsers(filteredItems.length)}
      </CardListSummaryBar>
      <div className="submission-status-list-container">
        <div className="submission-status-list">
          {groups.length > 0 ? (
            groups.map(group => (
              <div key={group.key} className="submission-group">
                <SubmissionGroupHeader
                  group={group}
                  onToggle={() => toggleGroup(group.key)}
                />
                {!group.isCollapsed && (
                  <div className="submission-group-items col">
                    {group.items.map(item => (
                      <UserSubmissionRow
                        key={item.id}
                        item={item}
                        template={template}
                        stepId={stepId}
                        projectMembersStore={projectMembersStore}
                        companiesStore={companiesStore}
                        locationAttributesStore={locationAttributesStore}
                        materialsStore={materialsStore}
                        projectDateStore={projectDateStore}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text large light center py12">
              {Localization.translator.noResults}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default inject(
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'materialsStore',
  'projectDateStore',
  'state',
  'userProjectsStore',
)(observer(SubmissionStatusList))
